import { z } from 'zod';
import { PROJECT_KEY_MAX, ProjectColor, ProjectMethodology, ProjectTabLayout } from './project.js';
/**
 * Sửa cấu hình dự án trưng bày, và giữ nó qua mỗi lượt dựng lại — APM-DEMO-p.
 *
 * ## Vì sao không sửa thẳng dự án
 *
 * Tổ chức demo bị xoá sạch và gieo lại mỗi nửa giờ. Mọi thứ sửa trên hàng
 * `project` sống được đúng tới lượt sau — đó là lý do màu dự án từng phải viết
 * cứng vào kịch bản mới xuất hiện được. Bảng override nằm ngoài tầm xoá ấy, và
 * lượt gieo đọc nó rồi đắp lên kịch bản.
 *
 * ## Vì sao ngày là **độ lệch**, không phải ngày thật
 *
 * Kịch bản khai mọi mốc bằng số ngày so với lượt gieo, và chính điều đó giữ cho
 * bộ trưng bày không cũ đi. Ghi một ngày tuyệt đối vào đây là đóng đinh một dự
 * án tại chỗ trong khi mọi thứ quanh nó trôi theo — sau một tuần nó bắt đầu sau
 * khi đã kết thúc.
 */
export const DemoProjectOverrideInput = z.object({
    /**
     * `null` = **bỏ đè**, quay về giá trị của kịch bản. `undefined` (vắng mặt) =
     * giữ nguyên thứ đang đè.
     *
     * Hai thứ đó khác nhau, và gộp lại thì không có cách nào xoá một ô đã đè:
     * gửi chuỗi rỗng sẽ thành một cái tên rỗng chứ không phải "trả về mặc định".
     */
    key: z
        .string()
        .min(2)
        // Cùng giới hạn với dự án thật — phần đè sinh ra dự án thật.
        .max(PROJECT_KEY_MAX, `Mã dự án tối đa ${PROJECT_KEY_MAX} ký tự`)
        .regex(/^[A-Z][A-Z0-9]*$/, 'Mã dự án viết hoa, không dấu cách')
        .nullish(),
    name: z.string().trim().min(1).max(200).nullish(),
    description: z.string().max(2_000).trim().nullish(),
    color: ProjectColor.nullish(),
    methodology: ProjectMethodology.nullish(),
    /** Âm = trong quá khứ. `-90` là "bắt đầu cách đây 90 ngày". */
    startOffset: z.number().int().min(-1_000).max(1_000).nullish(),
    endOffset: z.number().int().min(-1_000).max(1_000).nullish(),
    manMonths: z.number().min(0.5).max(500).nullish(),
    openEnded: z.boolean().nullish(),
    /** Chỉ số người trong danh sách thành viên của kịch bản. */
    pm: z.number().int().min(0).max(50).nullish(),
    contributors: z.array(z.number().int().min(0).max(50)).max(20).nullish(),
    tabLayout: ProjectTabLayout.nullish(),
});
/** Giá trị đang có hiệu lực cho một dự án của kịch bản, kèm phần bị đè. */
export const DemoScenarioProject = z.object({
    /** Mã trong kịch bản — định danh bền, dùng ở URL. Không đè được. */
    scenarioKey: z.string(),
    /** Kịch bản khai gì (để màn hình nói "mặc định: …"). */
    scenario: z.object({
        key: z.string(),
        name: z.string(),
        description: z.string(),
        color: ProjectColor,
        methodology: ProjectMethodology,
        startOffset: z.number().int(),
        endOffset: z.number().int(),
        manMonths: z.number(),
        openEnded: z.boolean(),
        pm: z.number().int(),
        contributors: z.array(z.number().int()),
    }),
    /** Những ô đang bị đè. Ô vắng mặt = đang theo kịch bản. */
    override: DemoProjectOverrideInput,
    /** Ai sửa lần cuối, hoặc `null` khi chưa ai đè gì. */
    updatedAt: z.iso.datetime().nullable(),
});
/** Danh sách dự án của kịch bản + người có thể gán vào chúng. */
export const DemoScenarioView = z.object({
    hasDemoOrg: z.boolean(),
    projects: z.array(DemoScenarioProject),
    /** Thành viên của kịch bản theo đúng thứ tự chỉ số mà `pm`/`contributors` trỏ tới. */
    members: z.array(z.object({ index: z.number().int(), name: z.string() })),
});
//# sourceMappingURL=demo-override.js.map